import {
  ArrowDown,
  ArrowUpRight,
  Globe2,
  HeartHandshake,
  Mail,
  MessageCircle,
  Sparkles,
  Sprout,
} from "lucide-react";
import { Button, AppIcon, SectionHeading, TextLink } from "../../components/Ui";
import { Brand } from "../../components/Brand";
import { ProductGallery } from "../products/ProductGallery";
import type {
  ContentItem,
  LandingContent,
  Product,
  Statistic,
  NavigationItem,
  SocialLink,
} from "../../types/content";

const growthIcons = [Sprout, Sparkles, Globe2, HeartHandshake];
const growthKickers = [
  "Purposeful making",
  "Connected systems",
  "Continuous learning",
  "Shared direction",
];

export function HeroSection({ content }: { content: LandingContent["hero"] }) {
  if (!content.isVisible) return null;
  const words = content.title.split(" ");
  const accentStart = Math.max(2, Math.floor(words.length * 0.58));
  return (
    <section className="hero" id="top">
      <div className="hero-grid-lines" aria-hidden="true" />
      <div className="container hero-inner">
        <div className="hero-copy">
          <h1>
            {words.slice(0, accentStart).join(" ")}{" "}
            <em>{words.slice(accentStart).join(" ")}</em>
          </h1>
          <p>{content.description}</p>
          <div className="hero-actions">
            <Button href={content.primaryCtaUrl}>
              {content.primaryCtaText} <ArrowUpRight size={17} />
            </Button>
          </div>
        </div>
        <div className="hero-visual" aria-label="Steria House product ecosystem">
          <div className="visual-orbit orbit-one" />
          <div className="visual-orbit orbit-two" />
          <div className="visual-card visual-card-main">
            <span className="visual-code">SH/01</span>
            <div className="visual-mark">
              <i />
              <i />
              <i />
            </div>
            <p>Ideas shaped into products people can grow with.</p>
          </div>
          <div className="visual-chip chip-ai">
            <AppIcon name="brain" size={18} /> AI
          </div>
          <div className="visual-chip chip-learn">
            <AppIcon name="education" size={18} /> Learn
          </div>
        </div>
      </div>
      <div className="container hero-foot">
        <span>Independent product studio · Indonesia</span>
        <a href="#about">
          Explore our story <ArrowDown size={16} />
        </a>
      </div>
    </section>
  );
}

export function AboutSection({ content }: { content: LandingContent["about"] }) {
  if (!content.isVisible) return null;
  return (
    <section className="section about-section" id="about">
      <div className="container">
        <span className="index-label">01 / ABOUT US</span>
        <div className="about-layout" data-scroll-reveal="up">
          <h2>{content.title}</h2>
          <div className="about-copy">
            <p>{content.description}</p>
            <div className="about-tags">
              {["Technology", "Education", "AI"].map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProductsSection({
  content,
  products,
}: {
  content: LandingContent["products"];
  products: Product[];
}) {
  if (!content.isVisible) return null;
  const active = products.filter((product) => product.isActive).sort((a, b) => a.displayOrder - b.displayOrder);
  return (
    <section className="section products-section" id="products">
      <div className="container">
        <span className="index-label">03 / OUR PRODUCTS</span>
        <SectionHeading title={content.title} description={content.description} />
        <div className="product-list">
          {active.map((product, index) => (
            <article
              className={`product-showcase product-tone-${(index % 4) + 1}`}
              data-scroll-delay={Math.min(index, 4)}
              data-scroll-reveal="up"
              key={product.id}
            >
              <div className="product-meta">
                <div className="product-number">0{index + 1}</div>
                <div>
                  <div className="product-labels">
                    <span>{product.platform}</span>
                  </div>
                  <h3>{product.name}</h3>
                  <p>{product.shortDescription}</p>
                  <div className="product-links">
                    <TextLink to={`/projects/${product.slug}`}>Explore product</TextLink>
                    {product.websiteUrl && (
                      <a className="text-link" href={product.websiteUrl} target="_blank" rel="noreferrer">
                        Visit website <ArrowUpRight size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
              <ProductGallery images={product.images} productName={product.name} compact />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PhilosophySection({
  content,
  principles,
}: {
  content: LandingContent["philosophy"];
  principles: ContentItem[];
}) {
  if (!content.isVisible) return null;
  return (
    <section className="section philosophy-section" id="philosophy">
      <div className="container">
        <span className="index-label is-light">02 / OUR PHILOSOPHY</span>
        <div className="philosophy-story">
          <figure className="philosophy-visual" data-scroll-reveal="left">
            <img
              src="/wisteria-philosophy-v2.webp"
              alt="Cascading violet wisteria blossoms illuminated along a resilient living vine"
              width="1122"
              height="1402"
              loading="lazy"
              decoding="async"
            />
            <figcaption>
              <span>Wisteria</span>
              <p>Beauty in growth.</p>
            </figcaption>
          </figure>
          <div
            className="philosophy-origin"
            data-scroll-delay="1"
            data-scroll-reveal="right"
          >
            <span className="philosophy-origin-label">Why Steria</span>
            <div>
              <p className="philosophy-origin-mark" aria-label="Wisteria becomes Steria">
                <span>WI</span>STERIA
              </p>
              <h2>{content.title}</h2>
              <p className="philosophy-origin-copy">{content.description}</p>
            </div>
            <div className="philosophy-values" aria-label="Wisteria-inspired values">
              <span>Patient growth</span>
              <span>Beauty with purpose</span>
            </div>
          </div>
        </div>
        <div className="principle-grid">
          {principles
            .filter((item) => item.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item, index) => (
              <article
                data-scroll-delay={Math.min(index, 4)}
                data-scroll-reveal="up"
                key={item.id}
              >
                <span className="principle-number">0{index + 1}</span>
                <AppIcon name={item.icon} size={26} />
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
        </div>
      </div>
    </section>
  );
}

export function CapabilitiesSection({
  content,
  services,
}: {
  content: LandingContent["capabilities"];
  services: ContentItem[];
}) {
  if (!content.isVisible) return null;
  return (
    <section className="section capabilities-section" id="capabilities">
      <div className="container">
        <span className="index-label">04 / WHAT WE DO</span>
        <SectionHeading title={content.title} description={content.description} />
        <div className="capability-list">
          {services
            .filter((item) => item.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item, index) => (
              <article
                data-scroll-delay={Math.min(index, 4)}
                data-scroll-reveal="up"
                key={item.id}
              >
                <span>0{index + 1}</span>
                <div className="capability-icon">
                  <AppIcon name={item.icon} size={25} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <ArrowUpRight className="capability-arrow" size={20} />
              </article>
            ))}
        </div>
      </div>
    </section>
  );
}

export function StatisticsSection({
  content,
  statistics,
}: {
  content: LandingContent["statistics"];
  statistics: Statistic[];
}) {
  if (!content.isVisible) return null;
  return (
    <section className="statistics-section" id="impact">
      <div className="container stats-grid">
        <div className="stats-intro" data-scroll-reveal="left">
          <span className="eyebrow">{content.eyebrow}</span>
          <h2>{content.title.replace(/\.$/, "")}</h2>
          {content.description && !content.description.includes("Progress measured") && <p>{content.description}</p>}
        </div>
        {statistics
          .filter((stat) => stat.isActive)
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((stat, index) => {
            const GrowthIcon = growthIcons[index % growthIcons.length];
            return (
              <article
                className="stat-card"
                data-scroll-delay={Math.min(index, 4)}
                data-scroll-reveal="up"
                key={stat.id}
              >
                <div className="stat-card-top">
                  <span className="stat-card-icon" aria-hidden="true">
                    <GrowthIcon size={22} strokeWidth={1.7} />
                  </span>
                  <span className="stat-card-kicker">
                    {growthKickers[index % growthKickers.length]}
                  </span>
                </div>
                <div>
                  <h3>{stat.label}</h3>
                  <p>{stat.description}</p>
                </div>
              </article>
            );
          })}
      </div>
    </section>
  );
}

export function FinalCtaSection({
  content,
  socialLinks,
}: {
  content: LandingContent["cta"];
  socialLinks?: SocialLink[];
}) {
  if (!content.isVisible) return null;
  const waLink = socialLinks?.find(
    (item) =>
      item.platform.toLowerCase().includes("whatsapp") ||
      item.icon === "message-circle" ||
      item.url.includes("wa.me")
  );
  const waUrl = waLink?.url || "https://wa.me/6287778037633";

  return (
    <section className="section final-cta" id="contact">
      <div className="container cta-shell" data-scroll-reveal="up">
        <div className="cta-symbol" aria-hidden="true">
          <Globe2 size={60} strokeWidth={1.1} />
        </div>
        <div>
          <span className="eyebrow">{content.eyebrow}</span>
          <h2>{content.title}</h2>
          <p>{content.description}</p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
          <Button href="mailto:steriahouse@gmail.com">
            {content.primaryCtaText} <Mail size={17} />
          </Button>
          <Button href={waUrl}>
            WhatsApp Maulana <MessageCircle size={17} />
          </Button>
        </div>
      </div>
    </section>
  );
}

export function SiteFooter({
  content,
  navigation,
  products,
  socialLinks,
}: {
  content: LandingContent["footer"];
  navigation: NavigationItem[];
  products: Product[];
  socialLinks?: SocialLink[];
}) {
  if (!content.isVisible) return null;
  return (
    <footer className="site-footer">
      <div data-scroll-reveal="up">
        <div className="container footer-grid">
          <div className="footer-brand">
            <Brand />
            <p>{content.description}</p>
            <a href={`mailto:${content.email}`}>{content.email}</a>
            <a
              href="https://wa.me/6287778037633"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "6px" }}
            >
              <MessageCircle size={15} /> WhatsApp: Maulana (+6287778037633)
            </a>
          </div>
          <div className="footer-column">
            <h3>Explore</h3>
            {navigation
              .filter((item) => item.isActive)
              .map((item) => (
                <a key={item.id} href={item.url}>{item.label}</a>
              ))}
          </div>
          <div className="footer-column">
            <h3>Products</h3>
            {products
              .filter((item) => item.isActive)
              .map((item) => (
                <a key={item.id} href={`/projects/${item.slug}`}>{item.name}</a>
              ))}
          </div>
          {socialLinks && socialLinks.length > 0 && (
            <div className="footer-column">
              <h3>Contact & Social</h3>
              {socialLinks
                .filter((item) => item.isActive)
                .map((item) => (
                  <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.platform}
                  </a>
                ))}
            </div>
          )}
        </div>
        <div className="container footer-bottom">
          <span>Designed around real human needs.</span>
          <span>{content.copyright}</span>
        </div>
      </div>
    </footer>
  );
}
